import { useContext } from "react";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import * as Sentry from "@sentry/nextjs";

export interface GetImagenOptimizadaProps {
	src: string;
	width?: number | string;
	height?: number | string;
	resize?: string;
}

export const useImage = () => {
	const { country_code } = useContext(ConfigStateContext);

	const getImagenOptimizada = ({ src, width, height, resize }: GetImagenOptimizadaProps) => {
		if (!src) return ""

		width = typeof width === "number" ? width : 800
		height = typeof height === "number" ? height : 600

		const resizeImage = (url: string) => {
			if (!width || !height) return url
			const dimensions = `${Math.round(width)}x${Math.round(height)}`
			const resizeMode = resize ? resize : "outside"

			if (url.includes("cdn")) {
				const pattern = /cdn([1-9][0-9]*)/

				if(country_code === "CO" && url.includes('infocasas.com.uy')){
					url = url.replace('infocasas.com.uy', 'fincaraiz.com.co')
					url = url.replace(pattern, "cdn4");
				} else if(!url.includes('cdn4')){
					url = url.replace(pattern, "cdn2")
				}

				if (hasImgExtension(url)) {
					let segments = url.split("/")
					segments[segments.length - 1] = `th.${resizeMode}${dimensions}.`.concat(segments[segments.length - 1])
					url = segments.join("/")
				}
			} else if (url.includes("/site/") && !url.includes("OVFR_COL")) {
				const breakPointString = url.indexOf('/site/')
				const secondSplit = url.substring(breakPointString)
				url = `https://d3s5pkt10pk3ga.cloudfront.net/resizedImages/${dimensions}${secondSplit}`
			}

			return url
		};

		return resizeImage(src)

	};

	// INF-1813 — la URL de getImagenOptimizada trae los px CSS exactos, así que en una pantalla
	// 2x/3x el navegador estira una foto a 1x. El srcset le ofrece las mismas medidas × densidad
	// y elige solo. Sólo para el resizer `th.` del CDN (el único medido que acepta cualquier
	// medida); cualquier otra URL no cambia con las medidas y no gana nada.
	const getSrcSet = ({ src, width, height, resize }: GetImagenOptimizadaProps) => {
		if (typeof width !== "number" || typeof height !== "number" || width <= 1 || height <= 1) return undefined

		// El lado mayor y no el ancho: con `outside` el CDN cubre toda la caja, así que el ancho que
		// vuelve puede ser el alto × la proporción de la foto.
		const candidates = SRCSET_DENSITIES.filter(density => density === 1 || Math.max(width, height) * density <= SRCSET_MAX_WIDTH).map(density => ({
			density,
			url: getImagenOptimizada({ src, width: width * density, height: height * density, resize }),
		}))
		if (candidates.length < 2 || !candidates[0].url.includes("/th.") || candidates[0].url === candidates[1].url) return undefined

		return candidates.map(({ density, url }) => `${url} ${density}x`).join(", ")
	};

	const notifyError = ({
		property_id,
		image_url,
		image_id
	}) => {
		Sentry.withScope(scope => {
			scope.setTag("image_error", image_url);
			scope.setContext("image_error_context", {
				property_id,
				image_url,
				image_id
			});
			Sentry.captureException(new Error("image-custom-error"));
		});
	}

	return { getImagenOptimizada, getSrcSet, notifyError };
};

// Sin 3x (INF-2208): a la distancia de un celular no se distingue del 2x, y cada medida 3x es
// otra miniatura que la Lambda@Edge del CDN genera y factura (~194 mil por día, medido).
const SRCSET_DENSITIES = [1, 2];
// Más allá de esto el CDN (camino webp) agranda por encima del original: más bytes, cero nitidez.
const SRCSET_MAX_WIDTH = 2000;

const hasImgExtension = (src: string): boolean => /\.(jpg|png|JPG|PNG|JPEG|jpeg)/g.test(src);
